import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
} from "@mui/material";
import Api from "api";
import { setDialog, setLoading } from "slices/appSlice";
import { useDispatch } from "react-redux";
import { formErrorStrfromObj } from "utils/shared";

/**
 * Add code-group dialog for diagnosis/procedure rows.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
function AddCodeGroupFrom({
  open,
  onClose,
  healthPlanKey,
  codeGroupType,
  updateCodeGroupList,
  existingCodeGroupKeys = [],
}) {
  const [codeGroupList, setCodeGroupList] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const dispatch = useDispatch();

  const existingCodeGroupKeySet = useMemo(
    () => new Set(existingCodeGroupKeys.filter((key) => key != null)),
    [existingCodeGroupKeys]
  );

  const filteredCodeGroupList = useMemo(
    () =>
      codeGroupList.filter(
        (codeGroup) => !existingCodeGroupKeySet.has(codeGroup.codeGroupKey)
      ),
    [codeGroupList, existingCodeGroupKeySet]
  );
  const handleClose = () => {
    onClose();
    setSelectedGroups([]);
  };

  const handleAdd = () => {
    if (selectedGroups) {
      updateCodeGroupList(selectedGroups);
    }
    handleClose();
  };

  const fetchCodeGroups = async () => {
    const status = "Approved";
    try {
      dispatch(setLoading(true));

      let list = [];
      if (codeGroupType === "Diagnosis") {
        const { data } = await Api.diagnosisCode.getCodeGroupDiagnosisCodeList(
          healthPlanKey,
          status
        );
        list = data;
      } else {
        const { data } = await Api.procedureCode.getCodeGroupProcedureCodeList(
          healthPlanKey,
          status
        );
        list = data;
      }
      setCodeGroupList(list);
    } catch (error) {
      const message =
        formErrorStrfromObj(error) || "An error occurred during the fetch.";
      dispatch(setDialog({ message, title: "Error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };
  useEffect(() => {
    fetchCodeGroups();
  }, []);

  return (
    <Box>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Code Groups</DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ pt: 1 }}
            fullWidth
            multiple
            data-testid="code-group-autocomplete"
            getOptionKey={(option) => option.codeGroupKey}
            options={filteredCodeGroupList}
            getOptionLabel={(option) =>
              `${option.codeGroupName} - v(${option.codeGroupVersion}) - ${option.isVarianceOf ? "Variance" : "Non-Variance"}`
            }
            value={selectedGroups}
            onChange={(event, newValue) => setSelectedGroups(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select an option"
                placeholder="Choose an option"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            color="primary"
            disabled={!selectedGroups || selectedGroups.length === 0}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AddCodeGroupFrom;
